import cn from 'classnames';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import {
  Report,
  ReportType,
} from '@/shared/api/protocol-ts/model/dto_report_pb';
import {
  getSharedDCCTVolumeInfo,
  isFullHeadMprReport,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppSelector } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';
import { reportsModel } from '@/entities/reports';

import {
  medicalImageModel,
  SelectedUploadedMedicalImages,
} from '@/features/medicalImage';

import {
  FullHeadMprSliceImage,
  makeDefaultFullHeadMprSliceSelection,
  resolveEffectiveFullHeadMprSliceSelection,
  resolveToothSliceThicknessOptionsMm,
  useFullHeadMprAvailability,
  useFullHeadMprReportTiles,
  useFullHeadMprSliceGroups,
  useFullHeadMprSliceTileSrcs,
  useToothBasisSeries,
  useToothFullHeadMprSliceRecords,
  useToothSliceOptions,
  useToothSliceSourceState,
} from '@/widgets/FullHeadMPR';

import {
  markPreviewFullHeadMprSlicesFailed,
  markPreviewFullHeadMprSlicesSettled,
  registerPreviewFullHeadMprSlices,
  unregisterPreviewFullHeadMprSlices,
} from '../../lib';

import styles from './PreviewFullHeadMprSlices.module.scss';

type PreviewFullHeadMprSlicesProps = {
  blackAndWhiteMode: boolean;
  inverted: boolean;
  report: Report;
  toothID: string;
  className?: string;
  serverSlicesFallback?: ReactNode;
};

const makeTileLoadKey = (tile: { id: string; pngUrl: string }) =>
  `${tile.id}|${tile.pngUrl}`;

export const PreviewFullHeadMprSlices: FC<PreviewFullHeadMprSlicesProps> = (
  props,
) => {
  const {
    blackAndWhiteMode,
    inverted,
    report,
    toothID,
    className,
    serverSlicesFallback,
  } = props;

  const [loadedByTileKey, setLoadedByTileKey] = useState<
    Record<string, boolean>
  >({});

  const isFullHeadMprEnabled = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_FullHeadMPRs,
    ),
  );
  const fullHeadMprLoading = useAppSelector(
    reportsModel.selectors.selectFullHeadMprLoading(report.ID),
  );

  // NOTE: until the current organization's settings arrive, feature flags read as
  // false — treating that as "settled" would let typewriterReadyToPDF fire before
  // the slices even start rendering, and the global flag cannot be revoked once set.
  const areFeatureFlagsResolved = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationSettingsLoaded,
  );

  const availability = useFullHeadMprAvailability({
    patientID: report.PatientID,
    reportID: report.ID,
  });

  const sourceState = useToothSliceSourceState(toothID);
  const input = sourceState.source;
  const uploadedImages = useAppSelector(
    medicalImageModel.selectors.selectSelectedUploadedMedicalImages(toothID),
  );
  const [readyUploads, setReadyUploads] = useState<Record<string, boolean>>({});
  const toothInput = input?.kind === 'tooth' ? input : null;

  const isFullHeadMprReportMode = isFullHeadMprReport({
    flagEnabled: isFullHeadMprEnabled,
    reportType: report.Type,
  });

  const isUsingFullHeadMPR =
    areFeatureFlagsResolved &&
    isFullHeadMprReportMode &&
    availability !== 'unavailable';

  const isRenderable =
    areFeatureFlagsResolved &&
    isFullHeadMprReportMode &&
    availability === 'available' &&
    Boolean(toothInput);
  const isPendingAvailability =
    report.Type === ReportType.ReportType_CBCT_GP &&
    (!areFeatureFlagsResolved ||
      (isFullHeadMprEnabled &&
        (availability === 'pending' ||
          (availability === 'available' && sourceState.status === 'pending'))));

  const thicknessOptionsMm = resolveToothSliceThicknessOptionsMm(
    toothInput ? getSharedDCCTVolumeInfo(toothInput.volumeFileID) : null,
  );

  const { sliceOptions } = useToothSliceOptions({
    thicknessOptionsMm,
    toothID,
  });

  const recordsByTileID = useToothFullHeadMprSliceRecords(toothID);

  const {
    frames,
    loading,
    massCentroidMm,
    profiles,
    series,
    slabThicknessMm,
    sliceStepMm,
  } = useToothBasisSeries({
    input: isRenderable ? toothInput : null,
    isEnabled: isRenderable,
    isPanelBlocking: true,
    sliceOptions,
  });

  const fullHeadMprSliceGroups = useFullHeadMprSliceGroups({
    frames,
    series,
    slabThicknessMm,
    sliceOptions,
    sliceStepMm,
    toothID,
  });

  const defaultTiles = useMemo(
    () =>
      makeDefaultFullHeadMprSliceSelection({
        groups: fullHeadMprSliceGroups,
        massCentroidMm,
        profiles,
      }),
    [fullHeadMprSliceGroups, massCentroidMm, profiles],
  );

  const { hasSavedViews, savedViewsLoading, tiles } = useFullHeadMprReportTiles(
    {
      groups: fullHeadMprSliceGroups,
      isEnabled: isUsingFullHeadMPR && availability === 'available',
      recordsByTileID,
      toothID,
    },
  );

  const { selectedTiles } = useMemo(
    () =>
      resolveEffectiveFullHeadMprSliceSelection({
        defaultTiles,
        recordsByTileID,
        tiles,
      }),
    [defaultTiles, recordsByTileID, tiles],
  );

  const { isTileSrcSettled, resolveTileSrc } = useFullHeadMprSliceTileSrcs({
    recordsByTileID,
    tiles: selectedTiles,
  });

  const areSelectedTilesLoaded = selectedTiles.every((tile) =>
    Boolean(loadedByTileKey[makeTileLoadKey(tile)]),
  );

  const hasFailedSavedViews =
    isUsingFullHeadMPR &&
    (fullHeadMprLoading === 'failed' ||
      (hasSavedViews && savedViewsLoading === 'failed'));

  const isSettled =
    !isPendingAvailability &&
    (!isUsingFullHeadMPR || fullHeadMprLoading === 'succeeded') &&
    (!isUsingFullHeadMPR ||
      !hasSavedViews ||
      (savedViewsLoading === 'succeeded' && areSelectedTilesLoaded)) &&
    (!isUsingFullHeadMPR ||
      uploadedImages.every((image) => readyUploads[image.assetID])) &&
    (!isRenderable ||
      loading === 'failed' ||
      (loading === 'succeeded' && areSelectedTilesLoaded));

  useEffect(() => {
    if (hasFailedSavedViews) {
      markPreviewFullHeadMprSlicesFailed(toothID);
    } else if (isSettled) {
      markPreviewFullHeadMprSlicesSettled(toothID);
    } else {
      registerPreviewFullHeadMprSlices(toothID);
    }
  }, [hasFailedSavedViews, isSettled, toothID]);

  useEffect(() => {
    return () => unregisterPreviewFullHeadMprSlices(toothID);
  }, [toothID]);

  if (!isUsingFullHeadMPR) {
    return <>{serverSlicesFallback}</>;
  }

  const handleImageSettled = (tileLoadKey: string) => {
    setLoadedByTileKey((current) =>
      current[tileLoadKey] ? current : { ...current, [tileLoadKey]: true },
    );
  };

  return (
    <div
      className={cn(
        styles.container,
        blackAndWhiteMode && styles.grayscale,
        className,
      )}
    >
      {selectedTiles.map((tile) => {
        const group = fullHeadMprSliceGroups.find(
          (clientGroup) => clientGroup.seriesName === tile.seriesName,
        );

        // NOTE: a tile with stored view options renders (and load-tracks) only its
        // adjusted src — otherwise typewriter could snapshot the raw tile while the
        // adjustment is still baking on a canvas.
        if ((group || tile.kind === 'view') && !isTileSrcSettled(tile)) {
          return <div key={tile.id} className={styles.sliceWrapper} />;
        }

        return group || tile.kind === 'view' ? (
          <div key={tile.id} className={styles.sliceWrapper}>
            <FullHeadMprSliceImage
              group={group}
              src={resolveTileSrc(tile)}
              tile={tile}
              record={recordsByTileID[tile.id]}
              className={styles.staticTile}
              imageClassName={cn(inverted && styles.inverted)}
            />
            <img
              className={styles.loadTracker}
              src={resolveTileSrc(tile)}
              alt=""
              onLoad={() => handleImageSettled(makeTileLoadKey(tile))}
              onError={() => handleImageSettled(makeTileLoadKey(tile))}
            />
          </div>
        ) : null;
      })}
      <SelectedUploadedMedicalImages
        toothID={toothID}
        className={styles.staticTile}
        isReadOnly
        onImageReady={(assetID) =>
          setReadyUploads((current) =>
            current[assetID] ? current : { ...current, [assetID]: true },
          )
        }
      />
    </div>
  );
};
